// src/services/smartSearchApi.js
import axios from 'axios';
import { getJwtToken } from "../utils/auth";
import { getBaseUrl } from "../utils/apiClient";

const smartSearchClient = axios.create({
  baseURL: `${getBaseUrl()}/smartsearch`,
});

smartSearchClient.interceptors.request.use(async (config) => {
  const token = await getJwtToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => Promise.reject(error));

/**
 * Llama al endpoint /unified del backend de búsqueda semántica
 * @param {string} query
 * @param {{location?: string, type?: string, limit?: number}} filters
 */
export const unifiedSearch = async (query, filters = {}) => {
  try {
    const params = {
      query: query && query.trim(),
      limit: filters.limit || 6,
      ...(filters.location && { location: filters.location }),
      ...(filters.type && { type: filters.type }),
    };

    // Log de la request que se envía al backend
    console.log('🚀 [SMART SEARCH API] Request enviada:');
    console.log('   📝 Query:', params.query);
    console.log('   📍 Location:', params.location || 'sin ubicación');
    console.log('   🔢 Limit:', params.limit);
    console.log('   🏷️  Type:', params.type || 'sin filtro de tipo');
    console.log('   🌐 URL completa:', `${smartSearchClient.defaults.baseURL}/unified`);
    console.log('   📦 Params completos:', JSON.stringify(params, null, 2));

    const response = await smartSearchClient.get('/unified', { params });

    // Log de la respuesta del backend
    console.log('✅ [SMART SEARCH API] Respuesta del backend:');
    console.log('   📊 Status:', response.status);
    console.log('   📈 Headers:', response.headers);
    console.log('   💾 Data completa:', JSON.stringify(response.data, null, 2));
    console.log('   🔍 Tipo de data:', Array.isArray(response.data) ? 'Array' : typeof response.data);

    if (Array.isArray(response.data)) {
      console.log('   📋 Cantidad de resultados:', response.data.length);
    } else if (response.data.results) {
      console.log('   📋 Cantidad de resultados:', response.data.results.length);
    }

    return response.data;
  } catch (error) {
    // Log del error
    console.error('❌ [SMART SEARCH API] Error en la búsqueda:');
    console.error('   🚨 Mensaje:', error.message);
    console.error('   📍 Status:', error.response?.status);
    console.error('   💬 Response data:', error.response?.data);
    console.error('   🔗 URL:', error.config?.url);
    console.error('   📦 Params enviados:', error.config?.params);

    // Propagar error para que el caller maneje fallback
    throw error;
  }
};

/**
 * Clasifica una descripción de post en la categoría más probable usando Vertex AI.
 * @param {string} description - Texto descriptivo del post.
 * @param {{type?: string}} [options] - Filtro opcional de tipo de categoría (default: 'service').
 * @returns {Promise<{match: boolean, categoryId?: string, categoryName?: string, similarity?: number}>}
 */
export const classifyCategoryFromDescription = async (description, options = {}) => {
  const params = {
    description,
    ...(options.type && { type: options.type }),
  };
  const response = await smartSearchClient.get('/classify-category', { params });
  return response.data;
};

export default smartSearchClient;

